import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import axios from 'axios';
import { environment } from 'src/environments/environment';

/** Mirrors the server's rules so the screen can refuse early with the same words. */
export const KB_MAX_FILE_SIZE = 100 * 1024 * 1024;
export const KB_MAX_FILES_PER_UPLOAD = 20;
export const KB_BLOCKED_EXTENSIONS = [
  '.exe', '.msi', '.bat', '.cmd', '.com', '.scr', '.ps1', '.vbs', '.sh', '.app', '.dmg', '.apk', '.jar', '.js', '.dll',
];
export const KB_CHUNK_SIZE = 10 * 1024 * 1024;

export type KbVisibility = 'company' | 'department' | 'private';
export type KbItemType = 'documents' | 'images' | 'videos' | 'audios' | 'others' | 'links';

export interface KbMe {
  userId: string;
  userName: string;
  department: string;
  isAdmin: boolean;
  isHr: boolean;
  isHead: boolean;
  canCreateFolders: boolean;
  canSeeAllDepartments: boolean;
  departments: string[];
}

export interface KbFolder {
  _id: string;
  name: string;
  description: string;
  visibility: KbVisibility;
  department: string | null;
  parentFolderId: string | null;
  ancestors: string[];
  depth: number;
  itemCount: number;
  subFolderCount: number;
  creatorId: string;
  creatorName: string;
  creatorProfileImg: string | null;
  canWrite: boolean;
  canManage: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface KbVersion {
  _id: string;
  fileName: string;
  extension: string;
  size: number;
  fileUrl: string;
  note: string;
  uploadedById: string;
  uploadedByName: string;
  createdAt: string;
}

export interface KbItem {
  _id: string;
  folderId: string;
  kind: 'file' | 'link';
  title: string;
  description: string;
  tags: string[];
  fileName: string;
  extension: string;
  size: number;
  fileUrl: string;
  mediaType: KbItemType | string;
  versions: KbVersion[];
  link: { url: string; host: string; title: string; description: string; image: string; ok: boolean } | null;
  addedById: string;
  addedByName: string;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  canDelete: boolean;
  folder?: { _id: string; name: string; visibility: KbVisibility; department: string | null } | null;
  path?: string;
}

export interface KbPresigned {
  itemId: string;
  fileName: string;
  uniqueFileName: string;
  uploadId: string;
  urls: { partNumber: number; url: string }[];
  fileUrl: string;
}

export interface KbVersionPresigned {
  versionId: string;
  uploadId: string;
  urls: { partNumber: number; url: string }[];
}

/** What a single multipart upload needs from the browser side. */
export interface KbUploadPlan {
  file: File;
  urls: { partNumber: number; url: string }[];
  uploadId: string;
}

@Injectable({ providedIn: 'root' })
export class KnowledgeBaseService {
  private base = `${environment.host}knowledge-base`;

  constructor(private http: HttpClient) {}

  private get headers(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('guestToken')}` });
  }

  private params(obj: Record<string, string | number | undefined | null>): HttpParams {
    let p = new HttpParams();
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined && v !== null && String(v) !== '') p = p.set(k, String(v));
    }
    return p;
  }

  me(): Observable<any> {
    return this.http.get(`${this.base}/me`, { headers: this.headers });
  }

  // ---- folders ----
  listFolders(q: { parentFolderId?: string; visibility?: string; department?: string; q?: string } = {}): Observable<any> {
    return this.http.get(`${this.base}/folders`, { headers: this.headers, params: this.params(q) });
  }
  getFolder(folderId: string): Observable<any> {
    return this.http.get(`${this.base}/folders/${folderId}`, { headers: this.headers });
  }
  createFolder(body: Partial<KbFolder> & { name: string }): Observable<any> {
    return this.http.post(`${this.base}/folders`, body, { headers: this.headers });
  }
  updateFolder(folderId: string, body: Partial<KbFolder>): Observable<any> {
    return this.http.patch(`${this.base}/folders/${folderId}`, body, { headers: this.headers });
  }
  deleteFolder(folderId: string): Observable<any> {
    return this.http.delete(`${this.base}/folders/${folderId}`, { headers: this.headers });
  }

  // ---- items ----
  listItems(q: { folderId: string; type?: string; tag?: string; q?: string; sort?: string; page?: number; limit?: number }): Observable<any> {
    return this.http.get(`${this.base}/items`, { headers: this.headers, params: this.params(q) });
  }
  getItem(itemId: string): Observable<any> {
    return this.http.get(`${this.base}/items/${itemId}`, { headers: this.headers });
  }
  presign(folderId: string, files: File[]): Observable<any> {
    return this.http.post(
      `${this.base}/items/presigned-url`,
      {
        folderId,
        files: files.map((f) => ({ fileName: f.name, totalChunks: this.chunkCount(f), fileSize: f.size })),
      },
      { headers: this.headers }
    );
  }
  completeUpload(files: { itemId: string; uploadId: string; parts: { PartNumber: number; ETag: string }[] }[]): Observable<any> {
    return this.http.post(`${this.base}/items/complete-upload`, { files }, { headers: this.headers });
  }
  addLink(body: { folderId: string; url: string; title?: string; description?: string; tags?: string[] }): Observable<any> {
    return this.http.post(`${this.base}/items/link`, body, { headers: this.headers });
  }
  updateItem(itemId: string, body: { title?: string; description?: string; tags?: string[]; url?: string }): Observable<any> {
    return this.http.patch(`${this.base}/items/${itemId}`, body, { headers: this.headers });
  }
  moveItem(itemId: string, folderId: string): Observable<any> {
    return this.http.post(`${this.base}/items/${itemId}/move`, { folderId }, { headers: this.headers });
  }
  versionPresign(itemId: string, file: File, note: string): Observable<any> {
    return this.http.post(
      `${this.base}/items/${itemId}/versions/presigned-url`,
      { fileName: file.name, totalChunks: this.chunkCount(file), fileSize: file.size, note },
      { headers: this.headers }
    );
  }
  versionComplete(itemId: string, body: { versionId: string; uploadId: string; parts: { PartNumber: number; ETag: string }[] }): Observable<any> {
    return this.http.post(`${this.base}/items/${itemId}/versions/complete`, body, { headers: this.headers });
  }
  deleteItem(itemId: string): Observable<any> {
    return this.http.delete(`${this.base}/items/${itemId}`, { headers: this.headers });
  }
  search(q: string, type?: string): Observable<any> {
    return this.http.get(`${this.base}/search`, { headers: this.headers, params: this.params({ q, type }) });
  }
  recent(limit = 12): Observable<any> {
    return this.http.get(`${this.base}/recent`, { headers: this.headers, params: this.params({ limit }) });
  }

  // ---- browser side of the multipart upload ----

  chunkCount(file: File): number {
    return Math.max(1, Math.ceil(file.size / KB_CHUNK_SIZE));
  }

  /** The same checks the server makes, so a refusal shows before anything is sent. */
  rejectReason(file: File): string | null {
    const dot = file.name.lastIndexOf('.');
    const ext = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
    if (!ext) return `"${file.name}" has no file type; give it an extension such as .pdf`;
    if (KB_BLOCKED_EXTENSIONS.includes(ext)) return `"${file.name}" is a program file; the knowledge base takes documents, images, videos and audio`;
    if (file.size > KB_MAX_FILE_SIZE) return `"${file.name}" is over 100 MB; keep big files on Drive and save the link here`;
    return null;
  }

  /**
   * PUTs the file's parts to S3 and returns the ETags the server needs to assemble them.
   * `onProgress` receives 0–100.
   */
  async uploadParts(plan: KbUploadPlan, onProgress?: (percent: number) => void): Promise<{ PartNumber: number; ETag: string }[]> {
    const { file, urls } = plan;
    const total = urls.length;
    const parts: { PartNumber: number; ETag: string }[] = [];
    for (let i = 0; i < total; i++) {
      const start = i * KB_CHUNK_SIZE;
      const chunk = file.slice(start, Math.min(start + KB_CHUNK_SIZE, file.size));
      const url = urls[i]?.url;
      if (!url) throw new Error(`Missing upload address for part ${i + 1} of "${file.name}"`);
      const response = await this.putWithRetry(url, chunk, (loaded) => {
        const fraction = chunk.size ? loaded / chunk.size : 1;
        onProgress?.(Math.min(99, Math.round(((i + fraction) / total) * 100)));
      });
      const raw = response?.headers?.etag ?? response?.headers?.['etag'] ?? response?.headers?.['x-amz-etag'];
      const etag = raw ? String(raw).replace(/"/g, '') : '';
      if (!etag) throw new Error(`S3 returned no ETag for part ${i + 1} of "${file.name}" (bucket CORS must expose ETag)`);
      parts.push({ PartNumber: i + 1, ETag: etag });
      onProgress?.(Math.round(((i + 1) / total) * 100));
    }
    return parts;
  }

  private async putWithRetry(url: string, chunk: Blob, onLoaded: (loaded: number) => void, attempts = 3): Promise<any> {
    let lastError: any;
    for (let attempt = 1; attempt <= attempts; attempt++) {
      try {
        return await axios.put(url, chunk, {
          headers: { 'Content-Type': 'application/octet-stream' },
          onUploadProgress: (e) => onLoaded(e.loaded ?? 0),
        });
      } catch (error) {
        lastError = error;
        if (attempt < attempts) await new Promise((r) => setTimeout(r, 600 * attempt));
      }
    }
    throw lastError;
  }
}

/** Human size: 1.2 MB, 340 KB. */
export function kbSize(bytes: number | undefined | null): string {
  const b = Number(bytes || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
